import { TrackingData } from "../api/api-types";
import { AdBreak } from "../public-types";
import { createLogger, Logger } from "../utils/log";
import { AdCallbacks, PrismAd } from "./PrismAd";
import {
  convertTrackingEventsAndImpressionsToMap,
  trackEvent,
  TrackingEventsMap,
  TrackingEventsMapKey,
} from "./tracking-events";

export type AdBreakCallbacks = {
  onAdBreakStart: (adBreak: AdBreak) => void;
  onAdBreakEnd: (adBreak: AdBreak) => void;
};

type PrismAdBreakOptions = {
  trackingData: TrackingData;
  callbacks: AdBreakCallbacks & AdCallbacks;
};

export class PrismAdBreak {
  private logger: Logger;

  private trackingData: TrackingData;
  private callbacks: AdBreakCallbacks;
  private trackingEvents: TrackingEventsMap;

  private started = false;

  public ads: PrismAd[];
  public watched = false;

  constructor(options: PrismAdBreakOptions) {
    this.logger = createLogger(`break:${options.trackingData.breakStart}`);

    const { onAdBreakStart, onAdBreakEnd, ...adCallbacks } = options.callbacks;

    this.trackingData = options.trackingData;
    this.callbacks = { onAdBreakStart, onAdBreakEnd };
    this.trackingEvents = convertTrackingEventsAndImpressionsToMap(
      this.trackingData.trackingEvents,
      []
    );

    this.ads = this.trackingData.ads.map(
      (adData) =>
        new PrismAd({
          adData,
          callbacks: adCallbacks,
        })
    );
  }

  private trackEvent(eventType: TrackingEventsMapKey) {
    if (this.watched) {
      // ignore events after ad is watched
      return;
    }
    if (!this.started) {
      this.logger.warn("tracking attempted on inactive ad");
      return;
    }
    trackEvent(this.trackingEvents.get(eventType));
  }

  public deactivate() {
    if (!this.started) {
      this.watched = true;
    } else {
      this.logger.warn(`cannot deactivate started break`);
    }
  }

  public isStreamPositionInAdBreak(streamPosition: number): boolean {
    return (
      streamPosition >= this.trackingData.breakStart &&
      streamPosition < this.trackingData.breakEnd
    );
  }

  public getAdForStreamPosition(streamPosition: number): PrismAd | undefined {
    if (!this.isStreamPositionInAdBreak(streamPosition)) {
      return;
    }
    return this.ads.find((ad) => ad.isStreamPositionInAd(streamPosition));
  }

  public handleStart() {
    if (this.started) {
      this.logger.warn(`invalid handleStart, break already started`);
      return;
    }
    this.started = true;
    this.logger.info(`start`);
    this.callbacks.onAdBreakStart(this.publicAdBreak);

    this.trackEvent("breakStart");
  }

  public handleEnd() {
    if (!this.started) {
      this.logger.warn(`invalid handleEnd, break not started`);
      return;
    }
    this.logger.info(`end`);
    this.trackEvent("breakEnd");

    this.started = false;
    this.watched = true;
    this.callbacks.onAdBreakEnd(this.publicAdBreak);
  }

  public get publicAdBreak(): AdBreak {
    return {
      start: this.trackingData.breakStart,
      end: this.trackingData.breakEnd,
      ads: this.ads.map((ad) => ad.publicAd),
      watched: this.watched,
    };
  }
}
